import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries } from "lightweight-charts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useTheme } from "@/contexts/ThemeContext";

const OVERLAYS = [
  { id: "ema20",     label: "EMA 20",         color: "#F5C542" },
  { id: "ema50",     label: "EMA 50",         color: "#7FA8FF" },
  { id: "bollinger", label: "Bollinger 20,2", color: "#2D6BFF" },
];

const PERIODS = [
  { id: "1m", label: "1A" },
  { id: "3m", label: "3A" },
  { id: "6m", label: "6A" },
  { id: "1y", label: "1Y" },
  { id: "2y", label: "2Y" },
  { id: "all", label: "TÜMÜ" },
];

export default function StockDetailPage() {
  const { symbol } = useParams();
  const nav = useNavigate();
  const { theme } = useTheme();
  const [period, setPeriod] = useState("1y");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOverlays, setActiveOverlays] = useState(["ema20"]);
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  const toggleOverlay = (id) =>
    setActiveOverlays((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/symbol/${symbol}?period=${period}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => toast.error("Veri yüklenemedi"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [symbol, period]);

  useEffect(() => {
    if (!containerRef.current || !data?.bars?.length) return;

    const isDark = theme !== "light";
    const bg = isDark ? "#0F1B33" : "#FFFFFF";
    const text = isDark ? "#9AA8C7" : "#5A6B92";
    const grid = isDark ? "#1D2F55" : "#EEF3FB";
    const border = isDark ? "#2A3D66" : "#D8E0F0";

    const chart = createChart(containerRef.current, {
      layout: { background: { color: bg }, textColor: text, fontFamily: "Inter, sans-serif" },
      grid: { vertLines: { color: grid }, horzLines: { color: grid } },
      rightPriceScale: { borderColor: border },
      timeScale: { borderColor: border, timeVisible: false, secondsVisible: false },
      crosshair: { mode: 1 },
      localization: { locale: "tr-TR" },
      autoSize: true,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#46C06F", downColor: "#E5484D",
      borderUpColor: "#46C06F", borderDownColor: "#E5484D",
      wickUpColor: "#46C06F", wickDownColor: "#E5484D",
    });
    candleSeries.setData(data.bars.map((b) => ({
      time: b.time, open: b.open, high: b.high, low: b.low, close: b.close,
    })));

    // volume overlay
    const volSeries = chart.addSeries(HistogramSeries, {
      color: "#2A3D66", priceFormat: { type: "volume" }, priceScaleId: "vol",
    });
    volSeries.priceScale().applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });
    volSeries.setData(data.bars.map((b) => ({
      time: b.time, value: b.volume, color: b.close >= b.open ? "#46C06F55" : "#E5484D55",
    })));

    // EMA20 line
    if (activeOverlays.includes("ema20")) {
      const ema20 = ema(data.bars.map((b) => b.close), 20);
      const s = chart.addSeries(LineSeries, { color: "#F5C542", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
      s.setData(data.bars.map((b, i) => ({ time: b.time, value: ema20[i] })).filter((v) => v.value != null));
    }
    // EMA50 line
    if (activeOverlays.includes("ema50")) {
      const ema50 = ema(data.bars.map((b) => b.close), 50);
      const s = chart.addSeries(LineSeries, { color: "#7FA8FF", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
      s.setData(data.bars.map((b, i) => ({ time: b.time, value: ema50[i] })).filter((v) => v.value != null));
    }
    // Bollinger 20,2 (upper/mid/lower)
    if (activeOverlays.includes("bollinger")) {
      const closes = data.bars.map((b) => b.close);
      const { upper, middle, lower } = bollinger(closes, 20, 2);
      const cfg = { lineWidth: 1, priceLineVisible: false, lastValueVisible: false };
      const su = chart.addSeries(LineSeries, { ...cfg, color: "#2D6BFF" });
      const sm = chart.addSeries(LineSeries, { ...cfg, color: "#2D6BFF66" });
      const sl = chart.addSeries(LineSeries, { ...cfg, color: "#2D6BFF" });
      su.setData(data.bars.map((b, i) => ({ time: b.time, value: upper[i] })).filter((v) => v.value != null));
      sm.setData(data.bars.map((b, i) => ({ time: b.time, value: middle[i] })).filter((v) => v.value != null));
      sl.setData(data.bars.map((b, i) => ({ time: b.time, value: lower[i] })).filter((v) => v.value != null));
    }

    chart.timeScale().fitContent();
    chartRef.current = chart;
    const ro = new ResizeObserver(() => chart.applyOptions({}));
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); chart.remove(); };
  }, [data, theme, activeOverlays]);

  const addToWatchlist = async () => {
    try {
      await api.post("/watchlist", { symbol });
      toast.success(`${symbol} izleme listesine eklendi`);
    } catch { toast.error("Eklenemedi"); }
  };

  const ind = data?.indicators || {};
  const posClass = (v) => (v == null ? "text-text-primary" : v >= 0 ? "text-signal-up" : "text-signal-down");

  return (
    <div className="space-y-4" data-testid="stock-detail-modal">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-brand-blue">
        <ArrowLeft size={14} /> Geri
      </button>

      <div className="bg-bg-panel border border-bg-border rounded-xl p-4 sm:p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-black font-mono text-text-primary tracking-wider">{symbol}</h1>
            {data?.stale && (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border text-accent-yellow bg-accent-yellow/10 border-accent-yellow/30">
                <AlertCircle size={10} /> Bayat Veri
              </span>
            )}
          </div>
          <p className="text-sm text-text-secondary mt-1">{data?.name}</p>
        </div>
        {ind.close != null && (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-2xl font-bold font-mono text-text-primary">{fmt(ind.close)} ₺</div>
              <div className={`text-sm font-mono ${posClass(ind.pct_change)}`}>
                {ind.pct_change > 0 ? "+" : ""}{fmt(ind.pct_change)}%
              </div>
            </div>
            <button
              data-testid="stock-detail-watchlist-btn"
              onClick={addToWatchlist}
              className="bg-brand-blue text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 hover:bg-brand-blue/90"
            ><Star size={14} /> İzleme Listesine Ekle</button>
          </div>
        )}
      </div>

      <div className="bg-bg-panel border border-bg-border rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`timeframe-btn-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                period === p.id ? "bg-brand-blue text-white" : "text-text-secondary hover:bg-bg-hover"
              }`}
            >{p.label}</button>
          ))}
          <div className="ml-auto text-xs text-text-secondary flex items-center gap-2 flex-wrap">
            {OVERLAYS.map((o) => {
              const active = activeOverlays.includes(o.id);
              return (
                <button
                  key={o.id}
                  data-testid={`chart-overlay-${o.id}`}
                  onClick={() => toggleOverlay(o.id)}
                  className={`text-[11px] px-2 py-1 rounded border flex items-center gap-1.5 transition-colors ${
                    active
                      ? "border-current"
                      : "border-bg-border text-text-secondary opacity-60 hover:opacity-100"
                  }`}
                  style={active ? { color: o.color, background: `${o.color}15` } : {}}
                >
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: o.color }} />
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
        {loading ? (
          <div className="h-96 flex items-center justify-center"><div className="radar-loader" /></div>
        ) : (
          <div ref={containerRef} data-testid="stock-candlestick-chart" className="h-[480px] w-full" />
        )}
      </div>

      {/* Indicators grid */}
      {ind.close != null && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card label="RSI (14)" value={fmt(ind.rsi, 1)} hint={ind.rsi >= 70 ? "Aşırı Alım" : ind.rsi <= 30 ? "Aşırı Satım" : "Nötr"} />
          <Card label="MACD Sinyali" value={statusLabel(ind.macd_status)} colored={statusColor(ind.macd_status)} icon={ind.macd_status?.includes("bull") || ind.macd_status?.includes("golden") ? TrendingUp : TrendingDown} />
          <Card label="EMA 20 / 50" value={statusLabel(ind.ma_status)} colored={statusColor(ind.ma_status)} />
          <Card label="Bollinger" value={bbLabel(ind.bb_status)} />
          <Card label="Hacim (Bugün)" value={fmt(ind.volume, 0)} hint={ind.volume_ratio ? `${ind.volume_ratio.toFixed(2)}x ortalama` : ""} />
          <Card label="EMA 20" value={fmt(ind.ema20)} />
          <Card label="EMA 50" value={fmt(ind.ema50)} />
          <Card label="EMA 200" value={fmt(ind.ema200)} />
        </div>
      )}
    </div>
  );
}

function Card({ label, value, hint, colored, icon: Icon }) {
  return (
    <div className="bg-bg-panel border border-bg-border rounded-lg p-3 flex flex-col justify-between">
      <div className="text-[10px] uppercase tracking-wider text-text-secondary font-medium">{label}</div>
      <div className={`mt-1.5 text-base font-mono font-semibold flex items-center gap-1.5 ${colored || "text-text-primary"}`}>
        {Icon && <Icon size={14} />} {value ?? "-"}
      </div>
      {hint && <div className="text-[10px] text-text-secondary mt-1">{hint}</div>}
    </div>
  );
}
function fmt(v, dec = 2) {
  if (v == null) return "-";
  return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function statusLabel(s) {
  return ({
    golden_cross: "Altın Kesişim", death_cross: "Ölüm Kesişimi",
    bullish: "Yükseliş", bearish: "Düşüş",
  })[s] || "-";
}
function statusColor(s) {
  if (["bullish", "golden_cross"].includes(s)) return "text-signal-up";
  if (["bearish", "death_cross"].includes(s)) return "text-signal-down";
  return "text-text-secondary";
}
function bbLabel(s) {
  return ({ upper_break: "Üst Bant Kırılımı", lower_break: "Alt Bant Kırılımı", inside: "Bant İçinde" })[s] || "-";
}

function ema(arr, period) {
  const k = 2 / (period + 1);
  const out = new Array(arr.length).fill(null);
  let prev = null;
  for (let i = 0; i < arr.length; i++) {
    if (i < period - 1) continue;
    if (i === period - 1) {
      let sum = 0;
      for (let j = 0; j < period; j++) sum += arr[j];
      prev = sum / period; out[i] = prev; continue;
    }
    prev = arr[i] * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

function bollinger(arr, period = 20, mult = 2) {
  const n = arr.length;
  const upper = new Array(n).fill(null);
  const middle = new Array(n).fill(null);
  const lower = new Array(n).fill(null);
  for (let i = period - 1; i < n; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += arr[j];
    const mean = sum / period;
    let sq = 0;
    for (let j = i - period + 1; j <= i; j++) sq += (arr[j] - mean) ** 2;
    const sd = Math.sqrt(sq / period);
    middle[i] = mean;
    upper[i] = mean + mult * sd;
    lower[i] = mean - mult * sd;
  }
  return { upper, middle, lower };
}
